import React, { memo, useCallback, useMemo } from 'react';
import { TextFieldProps } from '@mui/material';
import { MobileDatePicker, MobileDatePickerProps } from '@mui/x-date-pickers/MobileDatePicker';
import { StaticDatePicker } from '@mui/x-date-pickers/StaticDatePicker';
import { DateValidationError, DateView } from '@mui/x-date-pickers/models';
import { useTranslate } from 'hooks/use-translate';
import { APP_FORMAT_DATE } from 'configs/const';
import { AppPickerField, asPickerTextFieldSlotProps } from 'components/app-picker-field';
import { getPickerValidationError } from 'components/app-picker-field/validation';
import {
  AppPickerActionBar,
  PICKER_MODAL_ACTIONS,
  PickerModalProvider,
  usePickerModal,
} from 'components/app-picker-modal';
import { convertToDate, DateValue, isDateLike } from 'utils/dates';
import { objectSlotProps } from 'utils/slot-props';

/**
 * App date picker on @mui/x-date-pickers (Phase D1; was KeyboardDatePicker
 * from @material-ui/pickers 3). The public contract of the 42 call sites is
 * unchanged: `value` may be an ISO string, a Date or null; `onChange` receives
 * an ISO string (or null / the invalid date the user typed, as before); the
 * text-field props the old keyboard picker accepted at the top level (label,
 * error, helperText, placeholder, onBlur, autoFocus, size, fullWidth,
 * inputVariant) are routed to the field.
 *
 * v3 props that changed name: `variant="static"` renders StaticDatePicker,
 * `autoOk` -> closeOnSelect, `disableToolbar` -> toolbar hidden,
 * `openTo="date"` -> "day".
 *
 * Phase D2 (x-date-pickers 9): the field is the app's own `<input>`-based
 * `AppPickerField` (the legacy field DOM is gone from the library), so it is
 * still a plain MUI TextField with the calendar button as end adornment, like
 * the v3 field. Its input / label / helper-text slot props take MUI 9's shape:
 * `slotProps.textField.slotProps.{input,inputLabel,htmlInput,formHelperText}`
 * (the removed `InputProps` / `InputLabelProps` / `inputProps` top-level props).
 *
 * Prompt 2a-fix (D-48, D-49): the popup is v3's modal again -- `MobileDatePicker`
 * on every pointer type, Cancel / OK bar, a day click moves the dialog's date
 * and only OK publishes it (`onChange`, then `onAccept`), Cancel / backdrop
 * discard (`components/app-picker-modal`). `onAccept` never fires from typing.
 * The field's error is the consumer's `error` or v3's built-in validation
 * (invalid / min / max / past / future, with v3's helper strings unless the
 * consumer gives a helper text); `shouldDisableDate` does not paint it.
 */
type PickerProps = Omit<
  MobileDatePickerProps,
  | 'value'
  | 'onChange'
  | 'format'
  | 'openTo'
  | 'label'
  | 'name'
  | 'autoFocus'
  | 'disabled'
  | 'readOnly'
  | 'inputRef'
  | 'slotProps'
> & {
  /** The picker's slot props; `textField` is typed as the TextField the app's field renders. */
  slotProps?: Omit<NonNullable<MobileDatePickerProps['slotProps']>, 'textField'> & {
    textField?: TextFieldProps;
  };
};

type FieldProps = Pick<
  TextFieldProps,
  | 'label'
  | 'error'
  | 'helperText'
  | 'placeholder'
  | 'onBlur'
  | 'autoFocus'
  | 'size'
  | 'fullWidth'
  | 'required'
  | 'name'
  | 'inputRef'
  | 'style'
  | 'className'
>;

export interface AppDatePickerProps extends PickerProps, FieldProps {
  value?: DateValue;
  onChange: (date: string | null) => void;
  format?: string;
  disabled?: boolean;
  readOnly?: boolean;
  /** v3 name kept: 'dialog' | 'inline' open a popup, 'static' renders the calendar inline. */
  variant?: 'dialog' | 'inline' | 'static';
  /** v3 name kept: close (and commit) on the first day click. */
  autoOk?: boolean;
  /** v3 name kept: hide the toolbar. */
  disableToolbar?: boolean;
  /** v3 name kept: 'date' is x-date-pickers' 'day'. */
  openTo?: 'date' | 'day' | 'month' | 'year';
  /** v3 name kept: variant of the underlying text field. */
  inputVariant?: TextFieldProps['variant'];
}

/**
 * Only an invalid Date passes through as it is: it is what the field publishes
 * while the user types, and giving it back keeps the typed text in the field.
 */
const toPickerValue = (value: DateValue): Date | null =>
  value instanceof Date ? value : isDateLike(value) ? convertToDate(value) : null;

export const AppDatePicker = memo<AppDatePickerProps>(
  ({
    onChange,
    value,
    format = APP_FORMAT_DATE,
    variant,
    autoOk,
    disableToolbar,
    openTo,
    inputVariant,
    label,
    error,
    helperText,
    placeholder,
    onBlur,
    autoFocus,
    size,
    fullWidth = true,
    required,
    name,
    inputRef,
    style,
    className,
    slots,
    slotProps,
    onAccept,
    onOpen,
    onClose,
    ...rest
  }) => {
    const { t } = useTranslate();
    const onChangeWrap = useCallback(
      (date: Date | null) => {
        if (isDateLike(date)) {
          return onChange(convertToDate(date).toISOString());
        }
        onChange(date as unknown as string | null);
      },
      [onChange],
    );
    const pickerValue = useMemo(() => toPickerValue(value), [value]);
    const modal = usePickerModal<DateValidationError>({
      value: pickerValue,
      onChange: onChangeWrap,
      onAccept,
      autoOk,
      onOpen,
      onClose,
    });
    const { minDate, maxDate, disablePast, disableFuture } = rest;
    const validationError = getPickerValidationError(pickerValue, {
      minDate,
      maxDate,
      disablePast,
      disableFuture,
    });
    const localeText = useMemo(
      () => ({
        todayButtonLabel: t('today'),
        okButtonLabel: t('ok'),
        cancelButtonLabel: t('cancel'),
      }),
      [t],
    );
    // N2 (T5): the modal's toolbar title in Hebrew below md (app-picker-modal)
    const modalLocaleText = useMemo(
      () => ({ ...localeText, ...modal.localeTextBelowMd }),
      [localeText, modal.localeTextBelowMd],
    );
    const view: DateView | undefined = openTo === 'date' ? 'day' : openTo;
    const textFieldSlot = slotProps?.textField;

    if (variant === 'static') {
      // v3's static wrapper: every selection publishes (`onChange`, `onAccept`), no draft.
      // `slots` is passed explicitly: D2 destructured it for the field slot of the
      // dialog branch and the static branch silently lost the consumer's `day`
      // slot (dialog-time-slots' custom Day) -- found by the verify suite (D-51).
      return (
        <StaticDatePicker
          {...rest}
          value={pickerValue}
          onChange={onChangeWrap}
          onAccept={onAccept}
          openTo={view}
          localeText={localeText}
          slots={slots}
          slotProps={{
            ...slotProps,
            toolbar: { hidden: Boolean(disableToolbar), ...slotProps?.toolbar },
            actionBar: { actions: [], ...slotProps?.actionBar },
          }}
        />
      );
    }

    return (
      <PickerModalProvider actions={modal.actions}>
        <MobileDatePicker
          {...rest}
          {...modal.pickerProps}
          format={format}
          openTo={view}
          label={label}
          name={name}
          autoFocus={autoFocus}
          inputRef={inputRef}
          localeText={modalLocaleText}
          slots={{ ...slots, field: AppPickerField, actionBar: AppPickerActionBar }}
          slotProps={{
            ...slotProps,
            toolbar: { hidden: Boolean(disableToolbar), ...slotProps?.toolbar },
            actionBar: { actions: PICKER_MODAL_ACTIONS, ...slotProps?.actionBar },
            dialog: { ...slotProps?.dialog, ...modal.dialogSlotProps },
            // v3's keyboard button had no edge; x-date-pickers' edge="end" pulls the field 12px narrower
            openPickerButton: { edge: false, ...slotProps?.openPickerButton },
            textField: asPickerTextFieldSlotProps({
              variant: inputVariant,
              error: Boolean(error) || Boolean(validationError),
              // v3: the consumer's helper text wins; the built-in string fills an empty one.
              helperText: helperText || validationError || helperText,
              onBlur,
              size,
              fullWidth,
              required,
              style,
              className,
              ...textFieldSlot,
              slotProps: {
                ...textFieldSlot?.slotProps,
                htmlInput: { placeholder, ...objectSlotProps(textFieldSlot?.slotProps?.htmlInput) },
              },
            }),
          }}
        />
      </PickerModalProvider>
    );
  },
);
